// web/src/lib/theme.ts
//
// Theme state for Coretel Ops.
//
// - Default is 'light' (James's preference — dark is opt-in).
// - Three options: 'light' | 'dark' | 'system'. 'system' follows OS preference
//   via prefers-color-scheme media query, and re-applies live if the user
//   changes their OS setting while the app is open.
// - State is stored in localStorage under 'coretel-theme'.
// - The initial theme is also applied by an inline <script> in index.html
//   before React hydrates, so there's no flash of the wrong theme on reload.
//
// Usage:
//   const { theme, setTheme } = useTheme();
//   setTheme('dark');   // applies + persists

import { useEffect, useState } from "react";

export type Theme = "light" | "dark" | "system";
const STORAGE_KEY = "coretel-theme";

function systemPrefersDark(): boolean {
  return (
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
  );
}

function applyEffectiveTheme(theme: Theme): void {
  const effective: "light" | "dark" =
    theme === "system" ? (systemPrefersDark() ? "dark" : "light") : theme;

  if (typeof document !== "undefined") {
    document.documentElement.classList.toggle("dark", effective === "dark");
    // color-scheme tells the browser how to render native form controls,
    // scrollbars, etc. — keeps the OS-level UI in sync with our palette.
    document.documentElement.style.colorScheme = effective;
  }
}

function readStoredTheme(): Theme {
  if (typeof localStorage === "undefined") return "light";
  const v = localStorage.getItem(STORAGE_KEY);
  return v === "dark" || v === "system" || v === "light" ? v : "light";
}

// ---------------------------------------------------------------------------
// Module-level singleton so every useTheme() consumer stays in sync.
// ---------------------------------------------------------------------------

const listeners = new Set<() => void>();
let currentTheme: Theme = readStoredTheme();

export function getTheme(): Theme {
  return currentTheme;
}

export function setTheme(theme: Theme): void {
  currentTheme = theme;
  if (typeof localStorage !== "undefined") {
    localStorage.setItem(STORAGE_KEY, theme);
  }
  applyEffectiveTheme(theme);
  listeners.forEach((l) => l());
}

export function useTheme(): { theme: Theme; setTheme: (t: Theme) => void } {
  const [theme, setLocal] = useState<Theme>(currentTheme);

  useEffect(() => {
    const listener = () => setLocal(currentTheme);
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  return { theme, setTheme };
}

// ---------------------------------------------------------------------------
// Bootstrap — apply the stored theme and subscribe to OS preference changes.
// Runs once on module import (i.e. at app startup, after the inline script
// in index.html has already done the first paint).
// ---------------------------------------------------------------------------

if (typeof window !== "undefined") {
  applyEffectiveTheme(currentTheme);

  const mql = window.matchMedia("(prefers-color-scheme: dark)");
  const onSystemChange = () => {
    if (currentTheme === "system") applyEffectiveTheme("system");
  };
  // Older Safari: addListener; everything else: addEventListener
  if (mql.addEventListener) {
    mql.addEventListener("change", onSystemChange);
  } else if ((mql as any).addListener) {
    (mql as any).addListener(onSystemChange);
  }
}
